/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthActions } from 'src/hooks/useAuthActions';
import { useToast } from 'src/hooks/use-toast';
import { UserService } from 'src/services/api/UserService';
import { OTPVerification } from 'src/components/OTPVerification';
import { useGenericOTP } from 'src/hooks/useGenericOTP';
import { trackEvent } from 'src/utils/analytics';

const Login = ({ isMobile, onAuthSuccess }) => {
  const { login } = useAuthActions();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [loginMethod, setLoginMethod] = useState(null); // null | 'email' | 'phone'
  const [contact, setContact] = useState('');
  const phoneLoginTrackedRef = useRef(false);

  // Clear localStorage when login page mounts
  useEffect(() => {
    if (!onAuthSuccess) {
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('Failed to clear localStorage on login page:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactOtpHook = useGenericOTP(
    contact,
    'contact',
    null,
    null,
    null,
    null,
    toast,
    'login',
    !!onAuthSuccess
  );

  // Watch for phone login success — fires tracking here because the phone path
  // auto-navigates on OTP verification without the user clicking "Sign In".
  useEffect(() => {
    if (contactOtpHook.verified && loginMethod === 'phone') {
      if (!phoneLoginTrackedRef.current) {
        phoneLoginTrackedRef.current = true;
        trackEvent('form_submit', 'Login', 'phone');
        trackEvent('form_success', 'Login', 'phone');
      }
      if (onAuthSuccess) {
        const timer = setTimeout(() => {
          onAuthSuccess();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [contactOtpHook.verified, loginMethod, onAuthSuccess]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const details = params.get('details');

    if (error) {
      if (
        error === 'not_registered' &&
        details === 'google_account_not_linked'
      ) {
        const message = 'Try another Google account or phone number';
        setApiError(message);
        toast({
          message: 'Try another Google account or phone number',
          variant: 'destructive',
        });
      } else {
        const genericMessage =
          details || 'Google sign-in failed. Please try again.';
        setApiError(genericMessage);
        toast({ message: genericMessage, variant: 'destructive' });
      }
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, toast]);

  const handleGoogleLogin = async () => {
    trackEvent('form_submit', 'Login', 'google');
    try {
      setIsLoading(true);
      setApiError('');
      const response = await UserService.googleAuth();
      const authUrl = response?.data?.auth_url || response?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error('Failed to get Google authentication URL');
      }
    } catch (error) {
      console.error('Google login error:', error);
      setApiError('Google login failed. Try again.');
      toast({
        message: 'Google login failed. Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Phone path is tracked in the useEffect above (OTP auto-fires before this
    // button is reachable); guard here to prevent a second pair of events.
    if (loginMethod !== 'phone') trackEvent('form_submit', 'Login', loginMethod);
    try {
      setIsLoading(true);
      setApiError('');

      if (loginMethod === 'email') {
        if (onAuthSuccess) {
          try {
            const currentPath = `${location.pathname}${location.search}${location.hash}`;
            sessionStorage.setItem('postAuthRedirectPath', currentPath);
          } catch (_) {}
        }
        await login({ email, password });
        trackEvent('form_success', 'Login', loginMethod);
        if (onAuthSuccess) onAuthSuccess();
      } else if (loginMethod === 'phone') {
        if (contactOtpHook.verified) {
          if (!phoneLoginTrackedRef.current) {
            phoneLoginTrackedRef.current = true;
            trackEvent('form_submit', 'Login', 'phone');
            trackEvent('form_success', 'Login', 'phone');
          }
          toast({
            message: 'You have been logged in successfully!',
            variant: 'success',
          });
          if (onAuthSuccess) onAuthSuccess();
        } else {
          throw new Error('Please verify your phone number first');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage =
        error.message || 'An error occurred during login. Please try again.';
      trackEvent('form_error', 'Login', loginMethod);
      setApiError(errorMessage);
      toast({ message: errorMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Method List (no loginMethod selected) ──
  if (!loginMethod) {
    return (
      <div
        className="tb-panel-enter"
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {/* Phone OTP */}
        <button
          className="tb-method-btn"
          onClick={() => setLoginMethod('phone')}
        >
          <div className="tb-method-icon phone">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <div>
            <span>Phone OTP</span>
            <br />
            <small>Sign in with your mobile number</small>
          </div>
        </button>

        {/* Email & Password */}
        <button
          className="tb-method-btn"
          onClick={() => setLoginMethod('email')}
        >
          <div className="tb-method-icon email">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div>
            <span>Email &amp; Password</span>
            <br />
            <small>Use your linked email credentials</small>
          </div>
        </button>

        {/* Divider */}
        <div className="tb-method-divider">
          <span>or</span>
        </div>

        {/* Google */}
        <button
          className="tb-method-btn"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <div className="tb-method-icon google">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="#EA4335"
                d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
              />
              <path
                fill="#4285F4"
                d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
              />
              <path
                fill="#FBBC05"
                d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
              />
              <path
                fill="#34A853"
                d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
              />
            </svg>
          </div>
          <div>
            <span>Continue with Google</span>
            <br />
            <small>Fast sign-in with your Google account</small>
          </div>
        </button>
      </div>
    );
  }

  // ── Phone OTP ──
  if (loginMethod === 'phone') {
    return (
      <div className="tb-panel-enter">
        <button
          type="button"
          className="tb-back-link"
          onClick={() => setLoginMethod(null)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <OTPVerification
            type="contact"
            register={(fieldName) => ({
              onChange: (e) => setContact(e.target.value),
              onBlur: () => {},
              name: fieldName,
              ref: () => {},
            })}
            errors={{}}
            value={contact}
            verified={contactOtpHook.verified}
            isOTPSent={contactOtpHook.isOTPSent}
            isOTPLoading={contactOtpHook.isOTPLoading}
            showOTPInput={contactOtpHook.showOTPInput}
            isVerifyingOTP={contactOtpHook.isVerifyingOTP}
            otpError={contactOtpHook.otpError}
            canResend={contactOtpHook.canResend}
            resendTimeout={contactOtpHook.resendTimeout}
            otpFocusTrigger={contactOtpHook.otpFocusTrigger}
            handleSendOTP={contactOtpHook.handleSendOTP}
            handleVerifyOTP={contactOtpHook.handleVerifyOTP}
          />

          {contactOtpHook.verified && (
            <button
              type="submit"
              className="tb-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="tb-spinner" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          )}
        </form>
      </div>
    );
  }

  // ── Email & Password ──
  if (loginMethod === 'email') {
    return (
      <div className="tb-panel-enter">
        <button
          type="button"
          className="tb-back-link"
          onClick={() => {
            setLoginMethod(null);
            setApiError('');
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          noValidate
        >
          {/* ── Email ── */}
          <div className="tb-field">
            <label htmlFor="email" className="tb-label">
              Email Address <span className="tb-req">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="john@example.com"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setApiError('');
              }}
              disabled={isLoading}
              className={`tb-input${apiError ? ' error' : ''}`}
              autoComplete="email"
            />
          </div>

          {/* ── Password ── */}
          <div className="tb-field">
            <label htmlFor="password" className="tb-label">
              Password <span className="tb-req">*</span>
            </label>
            <div className="tb-input-wrap">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setApiError('');
                }}
                disabled={isLoading}
                className={`tb-input${apiError ? ' error' : ''}`}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="tb-toggle-pw"
                onClick={() => setShowPassword((v) => !v)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {apiError && (
              <div className="tb-field-error">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{apiError}</span>
              </div>
            )}
            <Link to="/forgot-password" className="tb-forgot-link">
              Forgot password?
            </Link>
          </div>

          {/* ── Submit ── */}
          <button type="submit" className="tb-submit-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="tb-spinner" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    );
  }

  return null;
};

export default Login;
